import { useMemo, type ReactElement } from 'react';
import { Link } from '@tanstack/react-router';
import { Pencil } from 'lucide-react';
import { useRepertoireEntry } from '@/lib/queries/repertoire';
import { useMasteryLevels } from '@/lib/queries/reference-data';
import { useArrangementComments, usePracticeHistory } from '@/lib/queries/pieces';
import { useArrangementResources } from '@/lib/queries/resources';
import { MasteryBadge } from '@/components/molecules/mastery-badge';
import { StatTile } from '@/components/molecules/stat-tile';
import { Button } from '@/components/atoms/button';
import { Card } from '@/components/atoms/card';
import { Icon } from '@/components/atoms/icon';
import { Micro } from '@/components/atoms/micro';
import type { PracticeHistoryEntry, RepertoireEntry } from '@/lib/types';

const MILLIS_PER_DAY = 1000 * 60 * 60 * 24;

function formatDateLong(iso: string | null): string {
  if (!iso) return 'Never';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

function formatDateShort(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function daysSince(iso: string | null): number | null {
  if (!iso) return null;
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / MILLIS_PER_DAY));
}

export type RepertoireDetailPanelProps = {
  arrangementId: string;
};

export function RepertoireDetailPanel({ arrangementId }: RepertoireDetailPanelProps): ReactElement {
  const { data: entry, isLoading, isError } = useRepertoireEntry(arrangementId);
  const { data: masteryData } = useMasteryLevels();
  const { data: practiceHistory } = usePracticeHistory(arrangementId);
  const { data: comments } = useArrangementComments(arrangementId);
  const { data: resources } = useArrangementResources(arrangementId);

  const masterySortOrder = useMemo<number | null>(() => {
    if (!entry?.masteryLevelId) return null;
    const levels = masteryData?.content ?? [];
    return levels.find((level) => level.masteryLevelId === entry.masteryLevelId)?.sortOrder ?? null;
  }, [entry?.masteryLevelId, masteryData?.content]);

  if (isLoading) {
    return (
      <section
        data-repertoire-detail-panel
        data-arrangement-id={arrangementId}
        className="flex h-full items-center justify-center text-sm text-[var(--ink-3)]"
      >
        Loading…
      </section>
    );
  }

  if (isError || !entry) {
    return (
      <section
        data-repertoire-detail-panel
        data-arrangement-id={arrangementId}
        className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center"
      >
        <h2 className="font-display text-xl font-semibold text-[var(--ink)]">Arrangement not found</h2>
        <p className="text-sm text-[var(--ink-3)]">It may have been removed from your repertoire.</p>
      </section>
    );
  }

  const totalSessions = practiceHistory?.length ?? 0;
  const lastSession = practiceHistory?.[0];
  const daysSinceLast = daysSince(lastSession?.practicedAt ?? null);
  const latestAssessment = lastSession?.assessmentLevelName;

  return (
    <section
      data-repertoire-detail-panel
      data-arrangement-id={arrangementId}
      className="flex-1 overflow-y-auto nice-scroll bg-[var(--bg-surface)]"
    >
      <div className="max-w-[720px] mx-auto p-8">
        <DetailPanelHeader
          entry={entry}
          masterySortOrder={masterySortOrder}
        />

        <section className="mt-6">
          <Micro className="mb-3">At a Glance</Micro>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <StatTile label="Total Sessions" value={String(totalSessions)} icon="music" tone="neutral" />
            <StatTile
              label="Last Practiced"
              value={lastSession ? formatDateLong(lastSession.practicedAt) : 'Never'}
              tone="brand"
              delta={daysSinceLast != null ? `${daysSinceLast} day${daysSinceLast === 1 ? '' : 's'} ago` : undefined}
            />
            <StatTile label="Latest Assessment" value={latestAssessment || '—'} icon="flame" tone="warm" />
            <StatTile label="Mastery"           value={entry.masteryLevelName || 'Not set'} icon="flame" tone="warm" />
          </div>
        </section>

        <ArrangementInfoCard entry={entry} />

        <PracticeHistoryCard history={practiceHistory ?? []} />

        <ResourcesSummaryCard resourceCount={resources?.length ?? 0} arrangementId={arrangementId} />

        <NotesSummaryCard
          firstNote={comments?.[0]?.content ?? null}
          totalCount={comments?.length ?? 0}
          arrangementId={arrangementId}
        />
      </div>
    </section>
  );
}

function DetailPanelHeader({
  entry, masterySortOrder,
}: { entry: RepertoireEntry; masterySortOrder: number | null }): ReactElement {
  const eyebrow = [entry.formName, entry.keyName].filter(Boolean).join(' · ') || 'Arrangement';
  return (
    <header className="flex items-start justify-between gap-6">
      <div className="min-w-0">
        <Micro>{eyebrow}</Micro>
        <h1 className="font-display text-[44px] font-semibold tracking-tight mt-2 leading-[1] text-[var(--ink)]">
          {entry.pieceName}
        </h1>
        <div className="mt-3">
          <MasteryBadge sortOrder={masterySortOrder ?? undefined} />
        </div>
      </div>
      <Link
        to="/repertoire/$arrangementId/edit"
        params={{ arrangementId: entry.arrangementId }}
        className="shrink-0"
      >
        <Button size="sm">
          <Pencil className="h-4 w-4" /> Edit
        </Button>
      </Link>
    </header>
  );
}

function ArrangementInfoCard({ entry }: { entry: RepertoireEntry }): ReactElement {
  const rows: { label: string; value: string }[] = [
    { label: 'Arrangement',       value: entry.arrangementName || '—' },
    { label: 'Key Signature',     value: entry.keyName || '—' },
    { label: 'Form',              value: entry.formName || '—' },
    { label: 'Queue Status',      value: entry.queueStatusName || '—' },
    { label: 'Practice Tempo',    value: entry.practiceTempoBeats ? `${entry.practiceTempoBeats} bpm` : '—' },
    { label: 'Performance Tempo', value: entry.performanceTempoBeats ? `${entry.performanceTempoBeats} bpm` : '—' },
  ];
  return (
    <Card padding="lg" className="mt-6">
      <Micro className="mb-4">Arrangement Info</Micro>
      <div className="mt-4 grid grid-cols-2 gap-y-4 gap-x-2 md:grid-cols-3">
        {rows.map((row) => (
          <div key={row.label}>
            <Micro>{row.label}</Micro>
            <p className="mt-1 text-sm font-medium text-[var(--ink)]">{row.value}</p>
          </div>
        ))}
      </div>
    </Card>
  );
}

const RECENT_PRACTICE_LIMIT = 5;

function PracticeHistoryCard({ history }: { history: PracticeHistoryEntry[] }): ReactElement {
  const recent = history.slice(0, RECENT_PRACTICE_LIMIT);
  return (
    <Card padding="lg" className="mt-6">
      <div className="flex items-center justify-between mb-4">
        <Micro>Recent Practice</Micro>
        <Icon name="clock" size={16} className="text-[var(--ink-3)]" />
      </div>
      {recent.length === 0 ? (
        <p className="mt-3 text-sm text-[var(--ink-3)]">No practice sessions yet.</p>
      ) : (
        <ul className="mt-3 divide-y divide-[var(--rule)]">
          {recent.map((session) => (
            <li key={session.practiceSessionId} className="flex items-center justify-between py-2 text-sm">
              <div className="flex items-center gap-2 text-[var(--ink)]">
                <Icon name="play" size={16} className="text-[var(--brand)]" />
                <span>{formatDateShort(session.practicedAt)}</span>
                {session.instrumentName && (
                  <span className="text-xs text-[var(--ink-3)]">· {session.instrumentName}</span>
                )}
              </div>
              {session.durationMinutes != null && (
                <span className="text-xs font-medium text-[var(--ink-3)]">
                  {session.durationMinutes}m
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function ResourcesSummaryCard({
  resourceCount, arrangementId,
}: { resourceCount: number; arrangementId: string }): ReactElement {
  return (
    <Card padding="lg" className="mt-6">
      <div className="flex items-center justify-between">
        <Micro>Resources</Micro>
        <Link
          to="/repertoire/$arrangementId"
          params={{ arrangementId }}
          className="text-[12px] font-semibold text-[var(--ink-3)] hover:text-[var(--ink)]"
        >
          Open detail →
        </Link>
      </div>
      <p className="mt-3 text-sm text-[var(--ink-3)]">
        {resourceCount === 0
          ? 'No resources yet.'
          : `${resourceCount} resource${resourceCount === 1 ? '' : 's'} attached.`}
      </p>
    </Card>
  );
}

function NotesSummaryCard({
  firstNote, totalCount, arrangementId,
}: { firstNote: string | null; totalCount: number; arrangementId: string }): ReactElement {
  return (
    <Card padding="lg" className="mt-6">
      <div className="flex items-center justify-between">
        <Micro>Notes</Micro>
        <Link
          to="/repertoire/$arrangementId"
          params={{ arrangementId }}
          className="text-[12px] font-semibold text-[var(--ink-3)] hover:text-[var(--ink)]"
        >
          Open detail →
        </Link>
      </div>
      {firstNote ? (
        <p className="mt-3 text-[14px] text-[var(--ink-2)] leading-relaxed line-clamp-3">
          {firstNote}
        </p>
      ) : (
        <p className="mt-3 text-sm text-[var(--ink-3)]">No notes yet.</p>
      )}
      {totalCount > 1 && (
        <p className="mt-2 text-[11px] text-[var(--ink-3)]">
          + {totalCount - 1} more note{totalCount - 1 === 1 ? '' : 's'}
        </p>
      )}
    </Card>
  );
}
